// IT予算レポート — 経営会議用 A4 print report.
// PILOT D (Communication Reviewer, Jun 2026): rebuilt to the report standard
// 要旨 → 主要所見 → リスク → 提言 → 次のアクション → 明細, single teal accent,
// fixed table layout (no overflow), repeating table headers, safe page breaks.
import { computeMonthly, computeVendors } from './LicenseManagement';
import { groupBy, monthlyPurchases } from './assetCostReport';
import { formatDate } from '@/utils/datetime';

const yen = (n) => (typeof n === 'number' ? `¥${Math.round(n).toLocaleString('ja-JP')}` : '—');
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const bar = (w) => `<span style="display:inline-block;height:7px;border-radius:3px;background:#0F766E;width:${Math.max(2, Math.round(w * 100))}%"></span>`;

export function buildItBudgetReportHtml(licenses, assets) {
  const today = formatDate(new Date());
  const licVendors = computeVendors(licenses);
  const licMonthly = computeMonthly(licenses);
  const licSub = licVendors.reduce((s, r) => s + r.sub, 0);
  const licPer = licVendors.reduce((s, r) => s + r.per, 0);
  const dated = licenses.filter((l) => l.expiry_date)
    .sort((a, b) => String(a.expiry_date).localeCompare(String(b.expiry_date)));
  const renewals = dated.slice(0, 10);
  const expired = dated.filter((l) => l.expired);
  const soon = dated.filter((l) => !l.expired && typeof l.days_to_expiry === 'number' && l.days_to_expiry <= 60);
  const expiredCost = expired.reduce((s, l) => s + (l.cost || 0), 0);
  const soonCost = soon.reduce((s, l) => s + (l.cost || 0), 0);

  const durables = assets.filter((a) => (a.asset_type || 'durable') === 'durable' && a.status !== 'disposed');
  const withCost = durables.filter((a) => typeof a.cost === 'number' && a.cost > 0);
  const noCost = durables.length - withCost.length;
  const assetCost = withCost.reduce((s, a) => s + a.cost, 0);
  const assetBook = withCost.reduce((s, a) => s + (a.book_value || 0), 0);
  const monthlyDep = withCost.reduce((s, a) => s + (a.monthly_depreciation || 0), 0);
  const assetCats = groupBy(durables, (a) => (a.category || '').trim() || '（カテゴリ未設定）');
  const assetMonthly = monthlyPurchases(assets);
  const annualRun = licSub + monthlyDep * 12;
  const topVendor = licVendors.slice().sort((a, b) => (b.sub + b.per) - (a.sub + a.per))[0];
  const topVendorShare = topVendor && (licSub + licPer) > 0
    ? Math.round(((topVendor.sub + topVendor.per) / (licSub + licPer)) * 100) : 0;
  const aged = withCost.filter((a) => (a.book_value || 0) <= 0);

  const combined = (licMonthly.length ? licMonthly : assetMonthly.map((m) => ({ key: m.key, label: m.label.replace('20', '').replace('年', '/').replace('月', ''), sub: 0, per: 0 })))
    .map((m, i) => {
      const am = assetMonthly[i] || { cost: 0 };
      return { label: m.label, sub: m.sub, per: m.per, asset: am.cost, total: m.sub + m.per + am.cost };
    });
  const maxT = Math.max(1, ...combined.map((m) => m.total));
  const maxLV = Math.max(1, ...licVendors.map((r) => r.sub + r.per));
  const maxAC = Math.max(1, ...assetCats.map((r) => r.cost));

  const li = (s) => `<li>${s}</li>`;
  const summary = [
    `年間ランニングコストは <b>${yen(annualRun)}</b>（サブスク年間 ${yen(licSub)} ＋ 年間償却費 ${yen(monthlyDep * 12)}）。`,
    `ライセンス ${licenses.length}件、IT資産 ${durables.length}件を対象に集計。資産の取得価額合計は ${yen(assetCost)}、現在簿価は ${yen(assetBook)}。`,
    expired.length
      ? `<b style="color:#B91C1C">期限切れライセンスが ${expired.length}件（${yen(expiredCost)}）</b>あり、更新または解約の判断が必要。`
      : '期限切れのライセンスはなし。',
    soon.length ? `60日以内に更新期限を迎えるライセンスが ${soon.length}件（${yen(soonCost)}）。` : '60日以内の更新期限はなし。',
    topVendor ? `ベンダー集中度: ${esc(topVendor.vendor)} が全ライセンス費用の ${topVendorShare}% を占める。` : '',
  ].filter(Boolean).map(li).join('');

  const findings = [
    `月別ITコストのピークは ${yen(maxT)}／月。直近12ヶ月の推移は下表 6. を参照。`,
    `資産カテゴリで最も投資額が大きいのは「${esc((assetCats[0] || {}).key || '—')}」（${yen((assetCats[0] || {}).cost || 0)}）。`,
    noCost > 0 ? `金額未入力の資産が ${noCost}件あり、集計対象外。実際のコストは本レポートより大きい可能性がある。` : '全ての資産に取得価額が入力済み。',
    aged.length ? `簿価ゼロ（償却済み）の資産が ${aged.length}件。更新計画の検討対象。` : '簿価ゼロの資産はなし。',
  ].map(li).join('');

  const risks = [
    expired.length
      ? `<b>コンプライアンス:</b> 期限切れライセンス ${expired.length}件（${yen(expiredCost)}）を放置すると、監査時の指摘および利用停止のリスク。`
      : '',
    soon.length
      ? `<b>予算:</b> 60日以内に ${yen(soonCost)} の更新支出が発生見込み。当月予算に未計上の場合は超過。`
      : '',
    topVendorShare >= 50 && topVendor
      ? `<b>調達:</b> ${esc(topVendor.vendor)} への依存度 ${topVendorShare}%。値上げ時の影響が大きい。`
      : '',
    noCost > 0 ? `<b>可視性:</b> 金額未入力 ${noCost}件のため、償却費・簿価が実態より小さく見える。` : '',
  ].filter(Boolean).map(li).join('') || li('現時点で重大なリスクは検出されていません。');

  const recos = [
    expired.length ? `期限切れ ${expired.length}件を「更新」「解約」「代替」に分類し、解約分は年間 ${yen(expiredCost)} の削減見込み。` : '',
    soon.length ? `60日以内更新分 ${yen(soonCost)} を当月の予算申請に反映する。` : '',
    topVendorShare >= 50 ? '主要ベンダーとの複数年契約または代替製品の比較見積を取得する。' : '',
    noCost > 0 ? `金額未入力の資産 ${noCost}件に取得価額を登録し、償却計算の対象に含める。` : '',
    aged.length ? `償却済み資産 ${aged.length}件のリプレース時期を情報システムで棚卸しする。` : '',
  ].filter(Boolean).map(li).join('') || li('現行の運用を継続。次回レビュー時に再評価する。');

  const actions = [
    ['期限切れライセンスの更新／解約判断', '情報システム', '当月末'],
    ['60日以内更新分の予算計上', '情報システム／経理', '翌月上旬'],
    noCost > 0 ? ['資産の取得価額の登録', '情報システム', '翌月末'] : null,
    ['ITコストレポートの再作成とレビュー', '情報システム', '翌四半期'],
  ].filter(Boolean).map(([w, o, d]) => `<tr><td>${w}</td><td>${o}</td><td class="c">${d}</td></tr>`).join('');

  const licVendorRows = licVendors.map((r) => `<tr><td>${esc(r.vendor)}</td><td class="c">${r.count}</td><td class="r">${r.sub ? yen(r.sub) : '—'}</td><td class="r">${r.per ? yen(r.per) : '—'}</td><td class="r"><b>${yen(r.sub + r.per)}</b></td><td class="viz">${bar((r.sub + r.per) / maxLV)}</td></tr>`).join('');
  const renewalRows = renewals.map((l) => `<tr><td>${esc(l.name)}</td><td>${esc(l.vendor || '—')}</td><td class="c">${esc(String(l.expiry_date).slice(0, 10))}</td><td class="c">${l.expired ? '<b style="color:#B91C1C">期限切れ</b>' : (typeof l.days_to_expiry === 'number' ? `あと${l.days_to_expiry}日` : '—')}</td><td class="r">${yen(l.cost)}</td></tr>`).join('');
  const assetCatRows = assetCats.map((r) => `<tr><td>${esc(r.key)}</td><td class="c">${r.count}</td><td class="r">${yen(r.cost)}</td><td class="r">${yen(r.book)}</td><td class="viz">${bar(r.cost / maxAC)}</td></tr>`).join('');
  const combinedRows = combined.map((m) => `<tr><td>20${esc(String(m.label).replace('/', '年'))}月</td><td class="r">${m.sub ? yen(m.sub) : '—'}</td><td class="r">${m.per ? yen(m.per) : '—'}</td><td class="r">${m.asset ? yen(m.asset) : '—'}</td><td class="r"><b>${yen(m.total)}</b></td><td class="viz">${m.total ? bar(m.total / maxT) : ''}</td></tr>`).join('');
  const empty = (n) => `<tr><td colspan="${n}" class="c muted">対象データがありません</td></tr>`;

  return `<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8"><title>IT予算レポート ${today}</title>
<style>
  @page{size:A4;margin:16mm 14mm}
  body{font-family:'Hiragino Kaku Gothic ProN','Yu Gothic',Meiryo,-apple-system,sans-serif;color:#18181B;font-size:11px;line-height:1.55;margin:0;padding:0}
  h1{font-size:19px;margin:0;font-weight:600}
  h2{font-size:13px;margin:20px 0 6px;padding-bottom:4px;border-bottom:2px solid #0F766E;font-weight:600;page-break-after:avoid}
  .head{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #0F766E;padding-bottom:10px}
  .brand{font-size:10px;color:#71717A;letter-spacing:.1em;margin-bottom:2px}
  .meta{font-size:10px;color:#71717A;text-align:right;white-space:nowrap}
  .cards{display:flex;gap:8px;margin-top:14px}
  .card{flex:1;border:1px solid #E4E4E7;border-radius:6px;padding:8px 10px}
  .card p{margin:0;font-size:9px;color:#71717A}
  .card b{font-size:14px;font-weight:600}
  .card.hero{border-color:#0F766E;background:#F0FDFA}
  ul{margin:6px 0 0;padding-left:18px}
  li{margin-bottom:3px}
  table{width:100%;table-layout:fixed;border-collapse:collapse;margin-top:4px}
  th{background:#FAFAFA;text-align:left;font-size:9px;color:#3F3F46;font-weight:600;padding:4px 6px;border:1px solid #E4E4E7}
  td{padding:4px 6px;border:1px solid #E4E4E7;font-size:10px;word-break:break-word}
  td.r{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
  td.c{text-align:center;white-space:nowrap}
  td.viz{width:20%}
  .muted{color:#A1A1AA}
  .note{font-size:9px;color:#71717A;margin-top:14px;border-top:1px solid #E4E4E7;padding-top:6px}
  .section{break-inside:avoid-page}
  tr{page-break-inside:avoid}
  thead{display:table-header-group}
  @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
</style></head><body>
<div class="head">
  <div><div class="brand">SENSBEY ／ ITシステム管理</div><h1>IT予算レポート（ライセンス＋IT資産）</h1></div>
  <div class="meta">作成日: ${today}<br>対象: ライセンス${licenses.length}件 ／ IT資産${durables.length}件</div>
</div>

<div class="section">
<h2>1. 要旨</h2>
<div class="cards">
  <div class="card hero"><p>年間ランニングコスト概算</p><b>${yen(annualRun)}</b><p style="margin-top:2px">サブスク年間＋年間償却費</p></div>
  <div class="card"><p>ライセンス サブスク（年間）</p><b>${yen(licSub)}</b></div>
  <div class="card"><p>ライセンス 買い切り累計</p><b>${yen(licPer)}</b></div>
  <div class="card"><p>資産 取得価額 / 現在簿価</p><b>${yen(assetCost)}</b><p style="margin-top:2px">簿価 ${yen(assetBook)}</p></div>
</div>
<ul>${summary}</ul>
</div>

<div class="section"><h2>2. 主要所見</h2><ul>${findings}</ul></div>
<div class="section"><h2>3. リスク</h2><ul>${risks}</ul></div>
<div class="section"><h2>4. 提言</h2><ul>${recos}</ul></div>
<div class="section"><h2>5. 次のアクション</h2>
<table><thead><tr><th style="width:52%">対応事項</th><th style="width:28%">担当</th><th style="width:20%">期限</th></tr></thead><tbody>${actions}</tbody></table>
</div>

<h2>6. 月別ITコスト推移（過去12ヶ月・統合）</h2>
<table><thead><tr><th>月</th><th>サブスク(月額換算)</th><th>買い切り</th><th>資産購入</th><th>合計</th><th>推移</th></tr></thead><tbody>${combinedRows || empty(6)}</tbody></table>

<h2>7. ライセンス — ベンダー別コスト</h2>
<table><thead><tr><th>ベンダー</th><th style="width:8%">件数</th><th>サブスク(年間)</th><th>買い切り</th><th>合計</th><th>構成</th></tr></thead><tbody>${licVendorRows || empty(6)}</tbody></table>

<h2>8. ライセンス — 直近の更新期限（上位10件）</h2>
<table><thead><tr><th style="width:34%">ソフト名</th><th>ベンダー</th><th style="width:14%">更新期限</th><th style="width:14%">残日数</th><th style="width:16%">金額</th></tr></thead><tbody>${renewalRows || empty(5)}</tbody></table>

<h2>9. IT資産 — カテゴリ別コストと簿価</h2>
<table><thead><tr><th>カテゴリ</th><th style="width:8%">件数</th><th>取得価額</th><th>現在簿価</th><th>構成</th></tr></thead><tbody>${assetCatRows || empty(5)}</tbody></table>

<p class="note">注記: サブスクは登録金額を年間費用とみなし ÷12 で月額換算（購入日〜更新期限で計上）。買い切り・資産は購入月に一括計上。減価償却は定額法・月割（耐用年数未入力は対象外）。年間ランニングコスト概算＝サブスク年間＋月額償却×12。金額未入力のデータ（資産${noCost}件）は集計に含まれません。<br>
SENSBEY ／ ITシステム管理 · ${today} 時点のデータ（Sensbey Web App／統合業務プラットフォーム より出力）</p>
</body></html>`;
}
